import { Dialog, DialogPanel } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Card from '@app/components/card/card';
import CardContent from '@app/components/card/card-content';
import CardFooter from '@app/components/card/card-footer';
import PrimaryButton from '@app/components/primary-button';
import { useTranslate } from '@tolgee/react';

interface ErrorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  error: {
    title: string;
    message: string;
    actionText?: string;
    onAction?: () => void;
    contactPhone?: string | null;
  };
}

export default function ErrorDialog({ isOpen, onClose, error }: ErrorDialogProps) {
  const { t } = useTranslate();

  if (!isOpen) return null;

  const handleAction = () => {
    if (error.onAction) {
      error.onAction();
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} as="div" className="error-dialog relative z-50" onClose={onClose}>
      {/* Backdrop */}
      <div className="error-dialog__backdrop fixed inset-0 bg-black/30" aria-hidden="true" />

      {/* Full-screen container to center the panel */}
      <div className="error-dialog__overlay fixed inset-0 z-50 overflow-y-auto">
        <div className="error-dialog__container flex min-h-full items-center justify-center p-4">
          <DialogPanel className="error-dialog__panel w-full max-w-md">
            <Card className="card error-dialog__card">
              <div className="error-dialog__header card-header px-2 py-4 md:px-4">
                <div className="flex items-center gap-3">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />
                  <h3 className="text-lg font-semibold">{error.title}</h3>
                </div>
              </div>
              <CardContent className="error-dialog__content">
                <p className="error-dialog__message text-gray-600">{error.message}</p>
                {error.contactPhone && (
                  <div className="error-dialog__contact mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500 mb-1">{t('online-order.error.contact-label', 'Need help?')}</p>
                    <a
                      href={`tel:${error.contactPhone}`}
                      className="text-lg font-semibold text-indigo-600 hover:text-indigo-700"
                    >
                      {error.contactPhone}
                    </a>
                  </div>
                )}
              </CardContent>
              <CardFooter className="error-dialog__footer">
                <div className="error-dialog__actions flex w-full justify-end gap-3">
                  <PrimaryButton
                    className="error-dialog__button min-h-[44px] w-full bg-indigo-600 px-6 hover:bg-indigo-400 sm:w-auto"
                    onClick={handleAction}
                  >
                    {error.actionText || t('common.close', 'Close')}
                  </PrimaryButton>
                </div>
              </CardFooter>
            </Card>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
